import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, RefreshCw, MessageCircle, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function md(text) {
  if (!text) return null;
  return text.split("\n").map((l, i) => {
    const k = `L${i}-${l.slice(0, 24)}`;
    if (l.startsWith("## ")) return <h4 key={k} className="font-heading text-[16px] text-[#8B4513] mt-3 mb-1">{l.slice(3)}</h4>;
    if (l.startsWith("# ")) return <h3 key={k} className="font-heading text-lg text-[#5B3A0F] mt-3 mb-2">{l.slice(2)}</h3>;
    if (l.startsWith("- ") || l.startsWith("* ")) return <li key={k} className="text-sm text-[#3A2A0F] ml-6 list-disc">{l.slice(2)}</li>;
    if (!l.trim()) return <div key={k} className="h-2" />;
    return <p key={k} className="text-sm text-[#3A2A0F] leading-relaxed">{l}</p>;
  });
}

const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function MonthlyReportPanel({ profileId, profile }) {
  const [month, setMonth] = useState(currentMonth());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState(profile?.phone || "");
  const [phoneBusy, setPhoneBusy] = useState(false);

  const load = async (m = month) => {
    setLoading(true); setReport(null);
    try {
      const r = await axios.get(`${API}/api/profiles/${profileId}/monthly-report`,
        { params: { month: m } });
      setReport(r.data);
      if (r.data.phone) setPhone(r.data.phone);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [profileId, month]);

  const savePhone = async () => {
    const cleaned = phone.replace(/[^0-9+]/g, "");
    setPhoneBusy(true);
    try {
      await axios.patch(`${API}/api/profiles/${profileId}`, { phone: cleaned });
      setPhone(cleaned);
      toast.success("WhatsApp number saved");
      // refresh report to update phone in payload
      setReport((r) => r ? { ...r, phone: cleaned } : r);
    } catch (e) {
      toast.error("Failed to save number");
    } finally { setPhoneBusy(false); }
  };

  const sendWhatsApp = () => {
    if (!phone) { toast.error("Save a WhatsApp number first"); return; }
    const number = phone.replace(/[^0-9]/g, "");
    if (!number) { toast.error("Invalid WhatsApp number"); return; }
    const header = `🌙 *${profile?.name || "Native"}* — ${month} astrology report\n\n`;
    const body = (report?.report_markdown || "").slice(0, 3800);
    const footer = "\n\n— Dr. Tejakumar Galla · M.A. Astrology · BHMS";
    const text = encodeURIComponent(header + body + footer);
    window.open(`https://wa.me/${number}?text=${text}`, "_blank", "noopener");
  };

  return (
    <section className="border-2 border-[#B08A3E] rounded-md p-5 bg-gradient-to-br from-[#FBF3DC] to-[#F0E6C8] shadow-md space-y-4"
             data-testid="monthly-report-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#8B4513]" />
            <div className="text-[10px] uppercase tracking-[0.4em] font-mono text-[#8B4513]">
              Free Monthly Report · Telugu · auto-generated on the 1st
            </div>
          </div>
          <div className="font-heading text-xl text-[#3A2A0F] mt-1">
            {profile?.name || "Native"} — {month}
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <label className="flex flex-col gap-1">
            <span className="text-[9px] uppercase tracking-widest font-mono text-[#8B4513]">Month</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                   data-testid="monthly-report-month"
                   className="bg-white/80 border border-[#B08A3E]/40 focus:border-[#B08A3E] outline-none text-[#3A2A0F] font-mono text-xs px-2 py-1.5 rounded-sm" />
          </label>
          <button onClick={() => load(month)} disabled={loading}
                  data-testid="monthly-report-refresh"
                  className="border border-[#8B4513] text-[#8B4513] hover:bg-[#8B4513] hover:text-white font-mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-sm flex items-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Reload
          </button>
        </div>
      </div>

      {/* Phone number editor */}
      <div className="flex flex-wrap items-end gap-2 bg-white/60 border border-[#B08A3E]/40 rounded-sm p-3">
        <label className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <span className="text-[9px] uppercase tracking-widest font-mono text-[#8B4513]">
            WhatsApp number (with country code, e.g. +91XXXXXXXXXX)
          </span>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                 placeholder="+91 63005 92183"
                 data-testid="monthly-report-phone"
                 className="bg-white border border-[#B08A3E]/40 focus:border-[#B08A3E] outline-none text-[#3A2A0F] font-mono text-sm px-2 py-1.5 rounded-sm" />
        </label>
        <button onClick={savePhone} disabled={phoneBusy}
                data-testid="monthly-report-phone-save"
                className="border border-[#5C8A3F] text-[#5C8A3F] hover:bg-[#5C8A3F] hover:text-white font-mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-sm disabled:opacity-50">
          {phoneBusy ? "Saving…" : "Save number"}
        </button>
        <button onClick={sendWhatsApp}
                disabled={!phone || !report}
                data-testid="monthly-report-send-whatsapp"
                className="bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-mono text-[10px] uppercase tracking-widest px-4 py-2 rounded-sm hover:brightness-110 disabled:opacity-50 flex items-center gap-2">
          <MessageCircle size={12} /> Send to WhatsApp
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[#8B4513] font-mono text-sm py-4">
          <Loader2 size={14} className="animate-spin" /> Reading the sky for {month}…
        </div>
      )}

      {report && !loading && (
        <div className="bg-white/80 border border-[#B08A3E]/40 rounded-sm p-4"
             data-testid="monthly-report-body">
          {md(report.report_markdown)}
          <div className="text-[10px] italic font-mono text-[#8B4513] mt-3 pt-2 border-t border-[#B08A3E]/30">
            Generated {new Date(report.generated_at).toLocaleString()}
            {report.phone ? ` · WhatsApp on file: ${report.phone}` : " · no WhatsApp on file"}
          </div>
        </div>
      )}
    </section>
  );
}
